// The composer's safety switch: a small pill. Review stays quiet; Autopilot
// darkens slightly. No purple in the composer, so the send button keeps the
// accent. It reads and writes the organization's setting, not the chat's.
import { Check, ChevronDown } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';

import {
    COMPOSER_PILL_QUIET_CLASSES,
    COMPOSER_PILL_TRIGGER_CLASSES,
} from '@apps/web/components/ui/composer_pill';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Popover, PopoverAnchor } from '@apps/web/components/ui/popover';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    AGENCY_SCOPE_HINT,
    AGENCY_SCOPE_LABEL,
    APPROVAL_MODE_OPTIONS,
    APPROVAL_MODE_READ_ONLY_HINT,
    approvalModeChangedMessage,
} from '@apps/web/features/phoebe_chat_v3/model/approval_rules';
import { type WritableApprovalSettings } from '@apps/web/features/phoebe_chat_v3/model/approval_settings_port';
import { ApprovalRulesPopover } from '@apps/web/features/phoebe_chat_v3/ui/approval_rules_popover';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import { type AgentApprovalMode } from '@libraries/typescript/api_client';

export type AgentApprovalModeChoice = AgentApprovalMode;

const MODE_PRESENTATIONS = APPROVAL_MODE_OPTIONS;
const MODE_ORDER: ReadonlyArray<AgentApprovalModeChoice> = [
    'review',
    'autopilot',
    'auto',
];

// Why the rules popover opened, which decides what its save writes.
type RulesPurpose = 'select_auto' | 'edit';

const AUTONOMY_RANK: Readonly<Record<AgentApprovalModeChoice, number>> = {
    review: 0,
    auto: 1,
    autopilot: 2,
};

export type ApprovalModeControlProps = Readonly<{
    readonly settings: WritableApprovalSettings;
    readonly disabled?: boolean;
}>;

export function ApprovalModeControl({
    settings,
    disabled: disabledByOwner = false,
}: ApprovalModeControlProps): ReactNode {
    const { mode: currentMode, rules, canSave } = settings;
    const disabled = disabledByOwner || settings.saving;
    // The description follows the pointer or keyboard highlight, not the
    // selection, and falls back to the selected mode between rows.
    const [highlightedMode, setHighlightedMode] =
        useState<AgentApprovalModeChoice | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [confirmMode, setConfirmMode] =
        useState<AgentApprovalModeChoice | null>(null);
    const [requestedRulesPurpose, setRequestedRulesPurpose] =
        useState<RulesPurpose | null>(null);
    const [rulesPurpose, setRulesPurpose] = useState<RulesPurpose | null>(null);
    const [focusPillWhenEnabled, setFocusPillWhenEnabled] = useState(false);
    const pillTriggerRef = useRef<HTMLButtonElement>(null);
    const confirmItemRef = useRef<HTMLDivElement>(null);
    const describedMode = highlightedMode ?? currentMode;
    const availableModes = MODE_ORDER.filter(
        (mode) =>
            mode !== 'auto' || settings.autoAvailable || currentMode === 'auto',
    );
    useEffect(() => {
        if (!focusPillWhenEnabled || disabled) {
            return;
        }
        pillTriggerRef.current?.focus();
        setFocusPillWhenEnabled(false);
    }, [disabled, focusPillWhenEnabled]);
    useEffect(() => {
        if (confirmMode !== null) {
            confirmItemRef.current?.focus();
        }
    }, [confirmMode]);

    function requestPillFocus(): void {
        setFocusPillWhenEnabled(true);
        if (!disabled) {
            pillTriggerRef.current?.focus();
        }
    }
    function closeRulesPopover(): void {
        setRulesPurpose(null);
        requestPillFocus();
    }
    async function changeMode(
        mode: AgentApprovalModeChoice,
        instructions?: string,
    ): Promise<void> {
        const saved = await settings.save(
            instructions === undefined
                ? { agent_v3_approval_mode: mode }
                : {
                      agent_v3_approval_mode: mode,
                      agent_v3_approval_instructions: instructions,
                  },
        );
        if (saved) {
            toast.success(
                approvalModeChangedMessage(MODE_PRESENTATIONS[mode].label),
                {
                    persistNotification: true,
                    notificationType: 'settings_update',
                },
            );
        }
    }
    function saveRules(text: string): void {
        const purpose = rulesPurpose;
        closeRulesPopover();
        if (purpose === 'select_auto') {
            void changeMode('auto', text);
        } else {
            void settings.saveRules(text);
        }
    }
    function selectMode(event: Event, mode: AgentApprovalModeChoice): void {
        if (mode === currentMode) {
            return;
        }
        if (mode === 'auto' && !rules?.trim()) {
            setRequestedRulesPurpose('select_auto');
            return;
        }
        if (AUTONOMY_RANK[mode] > AUTONOMY_RANK[currentMode]) {
            event.preventDefault();
            setConfirmMode(mode);
            return;
        }
        void changeMode(mode);
    }
    function eventTargetsPill(target: EventTarget | null): boolean {
        return (
            target instanceof Node &&
            pillTriggerRef.current?.contains(target) === true
        );
    }
    return (
        <span className="relative inline-flex">
            <DropdownMenu
                open={menuOpen}
                onOpenChange={(open) => {
                    setMenuOpen(open);
                    if (!open) {
                        setHighlightedMode(null);
                        setConfirmMode(null);
                    }
                }}
            >
                <Tooltip
                    open={tooltipOpen && !menuOpen && rulesPurpose === null}
                    onOpenChange={setTooltipOpen}
                >
                    <TooltipTrigger asChild={true}>
                        <DropdownMenuTrigger
                            ref={pillTriggerRef}
                            data-testid="approval-mode-trigger"
                            aria-label={`${AGENCY_SCOPE_LABEL}: ${MODE_PRESENTATIONS[currentMode].label}`}
                            disabled={disabled}
                            className={cn(
                                COMPOSER_PILL_TRIGGER_CLASSES,
                                'data-[state=open]:[&_.approval-mode-chevron]:rotate-180',
                                currentMode === 'review'
                                    ? COMPOSER_PILL_QUIET_CLASSES
                                    : 'border-foreground/15 bg-foreground/[0.06] text-foreground hover:bg-foreground/[0.09] data-[state=open]:bg-foreground/[0.09]',
                            )}
                        >
                            {MODE_PRESENTATIONS[currentMode].label}
                            <ChevronDown
                                aria-hidden="true"
                                className="approval-mode-chevron size-3 shrink-0 opacity-50 transition-transform duration-[180ms] motion-reduce:transition-none"
                            />
                        </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                        {canSave
                            ? AGENCY_SCOPE_LABEL
                            : APPROVAL_MODE_READ_ONLY_HINT}
                    </TooltipContent>
                </Tooltip>
                <DropdownMenuContent
                    side="top"
                    align="start"
                    sideOffset={7}
                    className="w-[min(16rem,calc(100vw-1rem))] rounded-lg p-1 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_12px_30px_rgba(0,0,0,0.13)] duration-[180ms]"
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        if (requestedRulesPurpose !== null) {
                            setRulesPurpose(requestedRulesPurpose);
                            setRequestedRulesPurpose(null);
                            return;
                        }
                        requestPillFocus();
                    }}
                >
                    {confirmMode === null ? (
                        <Fragment>
                            <DropdownMenuLabel className="text-muted-foreground text-v3-chat-meta px-2 pt-1.5 pb-1 font-normal">
                                {AGENCY_SCOPE_HINT}
                            </DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={currentMode}>
                                {availableModes.map((mode) => (
                                    <DropdownMenuRadioItem
                                        key={mode}
                                        value={mode}
                                        disabled={!canSave}
                                        onSelect={(event) =>
                                            selectMode(event, mode)
                                        }
                                        onFocus={() => setHighlightedMode(mode)}
                                        onMouseEnter={() =>
                                            setHighlightedMode(mode)
                                        }
                                        className="text-v3-chat flex items-center gap-2 rounded-md px-2 py-[7px] pl-2 [&_[data-slot=dropdown-menu-radio-item-indicator]]:hidden"
                                    >
                                        <span
                                            className={cn(
                                                'flex-1',
                                                mode === currentMode
                                                    ? 'font-semibold'
                                                    : 'font-medium',
                                            )}
                                        >
                                            {MODE_PRESENTATIONS[mode].label}
                                        </span>
                                        <Check
                                            aria-hidden="true"
                                            className={cn(
                                                'size-4',
                                                mode === currentMode
                                                    ? 'opacity-100'
                                                    : 'opacity-0',
                                            )}
                                        />
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                            {currentMode === 'auto' ? (
                                <Fragment>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onSelect={() =>
                                            setRequestedRulesPurpose('edit')
                                        }
                                    >
                                        {canSave ? 'Edit rules' : 'View rules'}
                                    </DropdownMenuItem>
                                </Fragment>
                            ) : null}
                            {/* Fixed height: descriptions differ enough that the
                            card would otherwise resize under the pointer. */}
                            <div
                                aria-live="polite"
                                className="border-border/60 text-muted-foreground text-v3-chat-meta mx-1 mt-1 flex min-h-[calc(2lh+14px)] items-start border-t px-1 pt-2 pb-[5px]"
                            >
                                <span
                                    key={describedMode}
                                    className="phoebe-approval-description-in"
                                >
                                    {
                                        MODE_PRESENTATIONS[describedMode]
                                            .description
                                    }
                                </span>
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <div className="space-y-1 px-2 pt-1.5 pb-2">
                                <p className="text-v3-chat font-semibold">
                                    Apply to the whole agency?
                                </p>
                                <p className="text-muted-foreground text-v3-chat-meta">
                                    Phoebe will act on its own in every chat,
                                    for everyone.
                                </p>
                            </div>
                            <DropdownMenuItem
                                ref={confirmItemRef}
                                className="text-v3-chat font-semibold"
                                onSelect={() => void changeMode(confirmMode)}
                            >
                                Apply
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-v3-chat">
                                Cancel
                            </DropdownMenuItem>
                        </Fragment>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            <Popover
                open={rulesPurpose !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setRulesPurpose(null);
                    }
                }}
            >
                <PopoverAnchor asChild={true}>
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0"
                    />
                </PopoverAnchor>
                {rulesPurpose !== null ? (
                    <ApprovalRulesPopover
                        rules={rules}
                        canSave={canSave}
                        saveLabel={
                            rulesPurpose === 'select_auto'
                                ? 'Turn on Auto'
                                : 'Save'
                        }
                        onCloseAutoFocus={(event) => {
                            event.preventDefault();
                            requestPillFocus();
                        }}
                        onFocusOutside={(event) => {
                            if (eventTargetsPill(event.target)) {
                                event.preventDefault();
                            }
                        }}
                        onInteractOutside={(event) => {
                            if (eventTargetsPill(event.target)) {
                                event.preventDefault();
                            }
                        }}
                        onSave={saveRules}
                        onCancel={closeRulesPopover}
                    />
                ) : null}
            </Popover>
        </span>
    );
}
